import { useState, useRef, useEffect } from 'react';
import { categories } from '@/mocks/categories';

export default function HeroSection() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <section className="relative w-full min-h-screen flex flex-col items-center justify-start pt-24 md:pt-8 pb-20 px-4 md:px-6 overflow-hidden">
      <div className="absolute inset-0 z-0">
        <img
          src="https://readdy.ai/api/search-image?query=Warm%20terracotta%20and%20sand%20gradient%20abstract%20background%20with%20delicate%20woven%20textile%20texture%20pattern%2C%20subtle%20geometric%20lines%20intersecting%20organically%2C%20soft%20atmospheric%20light%20diffusion%2C%20sophisticated%20art%20gallery%20wall%20aesthetic%2C%20contemporary%20editorial%20backdrop%2C%20no%20text%2C%20elegant%20warm%20earthy%20palette&width=1600&height=900&seq=hero-bg-v2-001&orientation=landscape"
          alt=""
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-white/30 via-white/15 to-white/50"></div>
      </div>

      <div className="relative z-10 w-full max-w-7xl mx-auto flex flex-col items-center pt-4 md:pt-12">
        <div className="relative mb-10 md:mb-14" ref={dropdownRef}>
          <button
            onClick={() => setDropdownOpen(!dropdownOpen)}
            className="flex items-center gap-3 px-6 py-3 bg-white/90 backdrop-blur-sm rounded-full border border-foreground-200 text-foreground-800 font-label text-sm md:text-base font-medium cursor-pointer hover:bg-white transition-colors duration-200 whitespace-nowrap"
          >
            <span>Browse Categories</span>
            <i className={`${dropdownOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-lg transition-transform duration-200`}></i>
          </button>

          {dropdownOpen && (
            <div className="absolute top-full mt-2 left-1/2 -translate-x-1/2 w-64 bg-white rounded-2xl shadow-lg border border-background-200 py-2 z-50 max-h-80 overflow-y-auto">
              {categories.map((cat) => (
                <a
                  key={cat.seq}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="block px-5 py-2.5 text-sm text-foreground-800 hover:bg-accent-50 hover:text-foreground-950 transition-colors duration-150 cursor-pointer whitespace-nowrap"
                  onClick={() => setDropdownOpen(false)}
                >
                  {cat.name}
                </a>
              ))}
            </div>
          )}
        </div>

        <div className="flex flex-col lg:flex-row items-start gap-10 lg:gap-16 w-full">
          <div className="flex-1 w-full">
            <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl font-extrabold text-foreground-950 leading-[1.05] tracking-tight">
              Superbuy<br />
              Spreadsheet<br />
              <span className="text-primary-500">2026 Hub</span>
            </h1>
          </div>

          <div className="flex-1 w-full flex flex-col justify-start pt-2 lg:pt-4">
            <p className="text-foreground-600 text-base md:text-lg leading-relaxed mb-8 max-w-md">
              Discover the latest Superbuy Spreadsheet collections, organized product lists, buying guides, QC tips, and international shopping resources. Find better products, save research time, and make smarter purchasing decisions.
            </p>

            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-2 px-7 py-3.5 bg-foreground-950 text-background-50 rounded-lg font-label font-semibold text-sm hover:bg-foreground-800 transition-colors duration-200 cursor-pointer whitespace-nowrap"
              >
                Superbuy Sheet
                <i className="ri-arrow-right-up-line"></i>
              </a>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-2 px-7 py-3.5 bg-transparent text-foreground-800 font-label font-semibold text-sm border-b-2 border-primary-500 hover:text-primary-600 hover:border-primary-600 transition-colors duration-200 cursor-pointer whitespace-nowrap"
              >
                Buy It Now
                <i className="ri-shopping-bag-line"></i>
              </a>
            </div>
          </div>
        </div>

        <div className="w-full mt-16 md:mt-20">
          <div className="w-full h-[300px] md:h-[400px] rounded-3xl overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=Colorful%20diverse%20fashion%20product%20flat%20lay%20with%20vibrant%20sneakers%20in%20coral%20pink%20and%20mint%20green%2C%20patterned%20oversized%20hoodies%2C%20gold%20tone%20accessories%2C%20arranged%20in%20dynamic%20editorial%20grid%20on%20crisp%20white%20studio%20surface%2C%20bright%20daylight%20photography%2C%20high%20contrast%20commercial%20catalog%20style%2C%20energetic%20playful%20composition&width=1600&height=600&seq=hero-bottom-v2-002&orientation=landscape"
              alt="Superbuy Spreadsheet product collections showcase"
              title="Superbuy Spreadsheet product finds"
              className="w-full h-full object-cover object-top"
            />
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-center gap-3 mt-10">
          <span className="inline-flex items-center gap-1.5 px-4 py-2 bg-accent-50 rounded-full text-accent-900 text-xs font-label font-medium">
            <i className="ri-check-line"></i> Updated Resources
          </span>
          <span className="inline-flex items-center gap-1.5 px-4 py-2 bg-accent-50 rounded-full text-accent-900 text-xs font-label font-medium">
            <i className="ri-check-line"></i> Beginner Friendly Guides
          </span>
          <span className="inline-flex items-center gap-1.5 px-4 py-2 bg-accent-50 rounded-full text-accent-900 text-xs font-label font-medium">
            <i className="ri-check-line"></i> Product Research Tools
          </span>
          <span className="inline-flex items-center gap-1.5 px-4 py-2 bg-accent-50 rounded-full text-accent-900 text-xs font-label font-medium">
            <i className="ri-check-line"></i> Safety First Approach
          </span>
        </div>
      </div>
    </section>
  );
}